import type { components } from "./admin/schema.gen";

// A blog is a published blog post served by the blog backend's public,
// unauthenticated endpoints (published-only — drafts never appear here).
export type Blog = components["schemas"]["PostOut"];

// Soft sage→forest gradient used as a placeholder when a post has no featured
// image, mirroring the Grove design's full-bleed photo bands.
export const PHOTO_GRADIENT =
  "radial-gradient(125% 95% at 28% 16%, #cdd8ad 0%, #9db39f 50%, #6f8771 100%)";

export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

// Derive a short plain-text summary from rich-text body HTML when a post has no
// explicit excerpt.
export function summarise(html: string, maxLen = 180): string {
  const text = stripHtml(html);
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function excerptFor(post: Blog, maxLen = 180): string {
  return post.excerpt?.trim() || summarise(post.body_html, maxLen);
}

// Estimated reading time from body word count (~200 wpm), as shown in the meta.
export function readingTime(html: string): string {
  const words = stripHtml(html).split(" ").filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Uppercase, letter-spaced sans label row used for post meta (e.g.
// "LATEST · ANXIETY · 5 MIN READ"). Empty parts are dropped.
export function BlogMeta({
  parts,
  className = "",
}: {
  parts: (string | null | undefined)[];
  className?: string;
}) {
  return (
    <div
      className={`flex flex-wrap gap-x-3 gap-y-1 font-nunito font-bold text-[11px] md:text-xs uppercase tracking-[0.14em] text-sage-deep ${className}`}
    >
      {parts.filter(Boolean).map((p, i) => (
        <span key={i}>{p}</span>
      ))}
    </div>
  );
}

// Renders a post's featured image, falling back to the brand gradient with a
// quiet "Blog" caption when none is set.
export function BlogImage({
  post,
  className = "",
}: {
  post: Blog;
  className?: string;
}) {
  if (post.featured_image_url) {
    return (
      <img
        src={post.featured_image_url}
        alt=""
        className={`object-cover ${className}`}
      />
    );
  }
  return (
    <div
      className={`flex items-center justify-center text-center font-nunito font-semibold text-[11px] uppercase tracking-[0.14em] text-white/90 ${className}`}
      style={{ background: PHOTO_GRADIENT }}
    >
      Blog
    </div>
  );
}
